import React, { useEffect, useState } from "react";
import "../CSS/AccountDetails.css";
import Cookies from "js-cookie";

const API_URL =
  "https://advenwarecareerlink.com/acl/My_account/My_account_details.php";

const UPDATE_API_URL =
  "https://yourdomain.com/api/student/update_account.php";

const AccountDetails = () => {
  const [user, setUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // =========================================
  // FETCH ACCOUNT
  // =========================================

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        setError("");

        const userID = Cookies.get("userID");

        console.log("USER ID FROM COOKIE:", userID);

        if (!userID) {
          throw new Error(
            "User ID was not found. Please log in again."
          );
        }

        const response = await fetch(API_URL, {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },

          body: JSON.stringify({
            myID: userID,
          }),
        });

        if (!response.ok) {
          throw new Error(
            `Server returned ${response.status}`
          );
        }

        const data = await response.json();

        console.log("PHP ACCOUNT RESPONSE:", data);

        if (!Array.isArray(data)) {
          throw new Error(
            "Invalid account data received from server."
          );
        }

        if (data.length === 0) {
          throw new Error(
            "No account was found for this user."
          );
        }

        const student = data[0];

        const formattedUser = {
          fullName: student.student_name || "",

          userId: student.user_ID || "",

          systemCode:
            student.systemCode ||
            `ACL-${student.user_ID || ""}`,

          idNumber: student.ID || "",

          phone: student.phone_number || "",

          email: student.email || "",

          institute: student.institute || "",

          course: student.course || "",

          currentCounty: student.county || "",

          countyToBePlaced:
            student.placement_county || "",

          cvName: student.cv || "",

          cvUploaded: Boolean(student.cv),

          insurance:
            student.insurance_cover === true ||
            student.insurance_cover === 1 ||
            student.insurance_cover === "1" ||
            String(student.insurance_cover)
              .toLowerCase() === "yes",

          insuranceProvider:
            student.insurance_provider || "",

          attachmentStatus:
            student.attachment_status ||
            "Searching",

          profileCompletion:
            calculateProfileCompletion(student),

          registeredDate:
            student.registered_date ||
            "Not available",
        };

        setUser(formattedUser);
        setOriginalUser(formattedUser);

      } catch (err) {
        console.error(
          "ACCOUNT FETCH ERROR:",
          err
        );

        setError(
          err.message ||
          "Unable to load your account information. Please try again."
        );
      }
    };

    fetchAccount();
  }, []);

  // =========================================
  // CALCULATE PROFILE COMPLETION
  // =========================================

  const calculateProfileCompletion = (student) => {
    const fields = [
      student.student_name,
      student.ID,
      student.phone_number,
      student.email,
      student.institute,
      student.course,
      student.county,
      student.placement_county,
      student.cv,
      student.insurance_cover,
    ];

    const completed = fields.filter(
      (field) =>
        field !== null &&
        field !== undefined &&
        String(field).trim() !== ""
    ).length;

    return Math.round(
      (completed / fields.length) * 100
    );
  };

  // =========================================
  // HANDLE INPUT
  // =========================================

  const handleChange = (field, value) => {
    setUser((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // =========================================
  // SAVE PROFILE
  // =========================================

  const saveProfile = async () => {
    if (!user) return;

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const response = await fetch(
        UPDATE_API_URL,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },

          body: JSON.stringify({
            user_ID: user.userId,

            student_name: user.fullName,

            ID: user.idNumber,

            phone_number: user.phone,

            email: user.email,

            institute: user.institute,

            course: user.course,

            county: user.currentCounty,

            placement_county:
              user.countyToBePlaced,

            insurance_cover:
              user.insurance ? 1 : 0,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Server returned ${response.status}`
        );
      }

      const result = await response.json();

      console.log(
        "UPDATE ACCOUNT RESPONSE:",
        result
      );

      if (
        result.success === false ||
        result.status === "error"
      ) {
        throw new Error(
          result.message ||
          "The server could not save your changes."
        );
      }

      setOriginalUser(user);

      setMessage(
        "Your account details have been updated successfully."
      );

      setEditMode(false);

      setTimeout(() => {
        setMessage("");
      }, 3000);

    } catch (err) {
      console.error(
        "UPDATE ACCOUNT ERROR:",
        err
      );

      setError(
        err.message ||
        "Unable to save your changes."
      );

    } finally {
      setSaving(false);
    }
  };

  // =========================================
  // CANCEL EDIT
  // =========================================

  const cancelEdit = () => {
    setUser(originalUser);
    setEditMode(false);
    setError("");
    setMessage("");
  };

  // =========================================
  // VIEW CV / DOCX
  // =========================================

  const handleViewCV = () => {
    if (!user?.cvName) {
      setError(
        "There is no CV attached to this account."
      );

      return;
    }

    /*
      IMPORTANT:

      Change this URL to the actual folder
      where your PHP server stores CVs.

      Example:

      https://yourdomain.com/uploads/cvs/
    */

    const cvURL = `https://advenwarecareerlink.com/acl/My_account/${user.cvName}`;

    window.open(
      cvURL,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // =========================================
  // LOADING
  // =========================================

  if (!user && !error) {
    return (
      <div className="account-loading">
        <div className="account-spinner"></div>

        <p>
          Loading your account...
        </p>
      </div>
    );
  }

  // =========================================
  // ERROR
  // =========================================

  if (!user && error) {
    return (
      <div className="account-error-page">
        <div className="account-error-card">
          <div className="error-icon">
            !
          </div>

          <h2>
            Unable to load account
          </h2>

          <p>
            {error}
          </p>

          <button
            onClick={() =>
              window.location.reload()
            }
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">

      {/* =====================================
          SAVE MESSAGE
      ===================================== */}

      {message && (
        <div className="save-message">
          <span>✓</span>
          {message}
        </div>
      )}

      {/* =====================================
          ERROR MESSAGE
      ===================================== */}

      {error && (
        <div className="account-error-message">
          <span>!</span>

          {error}

          <button
            onClick={() => setError("")}
          >
            ×
          </button>
        </div>
      )}

      {/* =====================================
          PROFILE HEADER
      ===================================== */}

      <section className="profile-intro">

        <div className="profile-cover"></div>

        <div className="profile-main">

          <div className="profile-avatar">
            {user.fullName
              ? user.fullName
                  .split(" ")
                  .filter(Boolean)
                  .map(
                    (word) => word[0]
                  )
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()
              : "ST"}

            <span className="avatar-online"></span>
          </div>

          <div className="profile-intro-text">

            <div className="profile-name-row">

              <h1>
                {user.fullName}
              </h1>

              <span className="verified-badge">
                ✓
              </span>

            </div>

            <p className="profile-course">
              {user.course || "Student"}
            </p>

            <div className="profile-contact-row">

              <span>
                <span className="contact-icon">
                  ⌖
                </span>
                {user.currentCounty || "Not provided"}
              </span>

              <span>
                <span className="contact-icon">
                  ✉
                </span>
                {user.email || "Not provided"}
              </span>

              <span>
                <span className="contact-icon">
                  ☎
                </span>
                {user.phone || "Not provided"}
              </span>

            </div>

            <div className="profile-tags">

              {user.course && (
                <span>
                  {user.course}
                </span>
              )}

              {user.institute && (
                <span>
                  {user.institute}
                </span>
              )}

              <span>
                User ID: {user.userId}
              </span>

            </div>

          </div>

          {!editMode ? (
            <button
              className="edit-profile-button"
              onClick={() =>
                setEditMode(true)
              }
            >
              <span>✎</span>
              Edit Profile
            </button>
          ) : (
            <div className="edit-buttons">

              <button
                className="cancel-button"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                className="save-button"
                onClick={saveProfile}
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : "Save Changes"}
              </button>

            </div>
          )}

        </div>

      </section>

      {/* =====================================
          MAIN CONTENT
      ===================================== */}

      <div className="account-content-grid">

        {/* =====================================
            LEFT COLUMN
        ===================================== */}

        <div className="account-left-column">

          {/* =====================================
              PERSONAL INFORMATION
          ===================================== */}

          <section className="details-section">

            <SectionTitle
              number="01"
              title="Personal information"
              description="Your identification and contact details."
            />

            <div className="details-grid">

              <EditableDetail
                label="FULL NAME"
                value={user.fullName}
                editMode={editMode}
                onChange={(value) =>
                  handleChange(
                    "fullName",
                    value
                  )
                }
              />

              <EditableDetail
                label="ID / PASSPORT NUMBER"
                value={user.idNumber}
                editMode={editMode}
                onChange={(value) =>
                  handleChange(
                    "idNumber",
                    value
                  )
                }
              />

              <EditableDetail
                label="PHONE NUMBER"
                value={user.phone}
                editMode={editMode}
                onChange={(value) =>
                  handleChange(
                    "phone",
                    value
                  )
                }
              />

              <EditableDetail
                label="EMAIL ADDRESS"
                value={user.email}
                editMode={editMode}
                onChange={(value) =>
                  handleChange(
                    "email",
                    value
                  )
                }
                type="email"
              />

            </div>

          </section>

          {/* =====================================
              EDUCATION
          ===================================== */}

          <section className="details-section">

            <SectionTitle
              number="02"
              title="Education & institution"
              description="Your current academic information."
            />

            <div className="education-card">

              <div className="education-icon">
                🎓
              </div>

              <div className="education-info">

                <span>
                  INSTITUTION
                </span>

                {editMode ? (
                  <input
                    className="edit-input"
                    value={
                      user.institute || ""
                    }
                    onChange={(e) =>
                      handleChange(
                        "institute",
                        e.target.value
                      )
                    }
                  />
                ) : (
                  <strong>
                    {user.institute ||
                      "Not provided"}
                  </strong>
                )}

                {editMode ? (
                  <input
                    className="edit-input course-input"
                    value={
                      user.course || ""
                    }
                    onChange={(e) =>
                      handleChange(
                        "course",
                        e.target.value
                      )
                    }
                  />
                ) : (
                  <p>
                    {user.course ||
                      "Not provided"}
                  </p>
                )}

              </div>

              <div className="education-current">

                <span>
                  STATUS
                </span>

                <strong>
                  Current student
                </strong>

              </div>

            </div>

          </section>

          {/* =====================================
              ATTACHMENT PREFERENCES
          ===================================== */}

          <section className="details-section">

            <SectionTitle
              number="03"
              title="Attachment preferences"
              description="Where you would like to be placed."
            />

            <div className="placement-grid">

              {/* PREFERRED COUNTY */}

              <div className="placement-card">

                <div className="placement-icon">
                  ⌖
                </div>

                <span>
                  PREFERRED COUNTY
                </span>

                {editMode ? (
                  <select
                    value={
                      user.countyToBePlaced || ""
                    }
                    onChange={(e) =>
                      handleChange(
                        "countyToBePlaced",
                        e.target.value
                      )
                    }
                    className="edit-select"
                  >
                    <option value="">
                      Select county
                    </option>

                    <option>Nairobi</option>
                    <option>Kiambu</option>
                    <option>Mombasa</option>
                    <option>Nakuru</option>
                    <option>Kisumu</option>
                    <option>Uasin Gishu</option>
                    <option>Machakos</option>
                    <option>Nyeri</option>
                    <option>Meru</option>
                    <option>Kakamega</option>
                  </select>
                ) : (
                  <strong>
                    {user.countyToBePlaced ||
                      "Not provided"}
                  </strong>
                )}

                <small>
                  County where you want your attachment
                </small>

              </div>

              {/* CURRENT COUNTY */}

              <div className="placement-card">

                <div className="placement-icon">
                  ◎
                </div>

                <span>
                  CURRENT LOCATION
                </span>

                {editMode ? (
                  <select
                    value={
                      user.currentCounty || ""
                    }
                    onChange={(e) =>
                      handleChange(
                        "currentCounty",
                        e.target.value
                      )
                    }
                    className="edit-select"
                  >
                    <option value="">
                      Select county
                    </option>

                    <option>Nairobi</option>
                    <option>Kiambu</option>
                    <option>Mombasa</option>
                    <option>Nakuru</option>
                    <option>Kisumu</option>
                    <option>Uasin Gishu</option>
                    <option>Machakos</option>
                    <option>Nyeri</option>
                    <option>Meru</option>
                    <option>Kakamega</option>
                  </select>
                ) : (
                  <strong>
                    {user.currentCounty ||
                      "Not provided"}
                  </strong>
                )}

                <small>
                  Your current county
                </small>

              </div>

              {/* STATUS */}

              <div className="placement-card">

                <div className="placement-icon status-icon">
                  ✓
                </div>

                <span>
                  ATTACHMENT STATUS
                </span>

                <div className="searching-status">
                  <i></i>
                  {user.attachmentStatus}
                </div>

                <small>
                  We are looking for suitable
                  opportunities
                </small>

              </div>

            </div>

          </section>

          {/* =====================================
              DOCUMENTS
          ===================================== */}

          <section className="details-section">

            <SectionTitle
              number="04"
              title="Documents"
              description="Documents associated with your account."
            />

            <div className="document-card">

              <div className="document-icon">
                DOCX
              </div>

              <div className="document-info">

                <strong>
                  {user.cvName ||
                    "No CV uploaded"}
                </strong>

                <span>
                  Curriculum Vitae •{" "}
                  {user.cvUploaded
                    ? "Uploaded"
                    : "Not uploaded"}
                </span>

              </div>

              {user.cvUploaded && (
                <button
                  className="document-button"
                  onClick={handleViewCV}
                >
                  View CV
                </button>
              )}

              {editMode && (
                <label className="replace-cv">

                  Replace CV

                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {

                      const file =
                        e.target.files?.[0];

                      if (file) {
                        handleChange(
                          "cvName",
                          file.name
                        );

                        handleChange(
                          "cvFile",
                          file
                        );

                        handleChange(
                          "cvUploaded",
                          true
                        );
                      }
                    }}
                  />

                </label>
              )}

            </div>

          </section>

          {/* =====================================
              INSURANCE
          ===================================== */}

          <section className="details-section">

            <SectionTitle
              number="05"
              title="Insurance cover"
              description="Your insurance information for attachment."
            />

            <div
              className={
                user.insurance
                  ? "insurance-card insured"
                  : "insurance-card uninsured"
              }
            >

              <div className="insurance-symbol">
                {user.insurance
                  ? "✓"
                  : "!"}
              </div>

              <div className="insurance-info">

                <span>
                  INSURANCE STATUS
                </span>

                <strong>
                  {user.insurance
                    ? "You have insurance cover"
                    : "No insurance cover"}
                </strong>

                {user.insurance &&
                  user.insuranceProvider && (
                    <p>
                      Provider:{" "}
                      {user.insuranceProvider}
                    </p>
                  )}

              </div>

              {editMode && (
                <label className="insurance-toggle">

                  <input
                    type="checkbox"
                    checked={
                      user.insurance
                    }
                    onChange={(e) =>
                      handleChange(
                        "insurance",
                        e.target.checked
                      )
                    }
                  />

                  <span></span>

                </label>
              )}

              {!user.insurance &&
                !editMode && (
                  <button
                    className="insurance-button"
                  >
                    Get Insurance
                  </button>
                )}

            </div>

          </section>

        </div>

        {/* =====================================
            RIGHT COLUMN
        ===================================== */}

        <div className="account-right-column">

          {/* =====================================
              PROFILE COMPLETION
          ===================================== */}

          <section className="completion-card">

            <div className="completion-heading">

              <div className="completion-icon">
                ✓
              </div>

              <div>
                <span>
                  PROFILE COMPLETION
                </span>

                <strong>
                  {user.profileCompletion}%
                </strong>
              </div>

            </div>

            <div className="completion-ring">

              <svg
                viewBox="0 0 100 100"
              >
                <circle
                  className="ring-background"
                  cx="50"
                  cy="50"
                  r="42"
                />

                <circle
                  className="ring-progress"
                  cx="50"
                  cy="50"
                  r="42"
                  style={{
                    strokeDasharray: `${
                      user.profileCompletion *
                      2.64
                    } 264`,
                  }}
                />
              </svg>

              <strong>
                {user.profileCompletion}%
              </strong>

            </div>

            <div className="completion-text">

              <strong>
                Your profile is{" "}
                {user.profileCompletion}%
                complete
              </strong>

              <p>
                Keep your information updated to
                improve your chances of finding an
                attachment.
              </p>

            </div>

            <div className="completion-progress">

              <div
                className="completion-progress-bar"
                style={{
                  width: `${user.profileCompletion}%`,
                }}
              />

            </div>

          </section>

          {/* =====================================
              ACCOUNT INFORMATION
          ===================================== */}

          <section className="side-card">

            <div className="side-card-title">
              <div className="side-card-icon">
                #
              </div>

              <h3>
                Account information
              </h3>
            </div>

            <div className="account-meta">

              <div>
                <span>
                  USER ID
                </span>

                <strong>
                  {user.userId}
                </strong>
              </div>

              <div>
                <span>
                  ACCOUNT CREATED
                </span>

                <strong>
                  {user.registeredDate}
                </strong>
              </div>

              <div>
                <span>
                  ACCOUNT STATUS
                </span>

                <strong className="active-account">
                  ● Active
                </strong>
              </div>

            </div>

          </section>

          {/* =====================================
              ATTACHMENT STATUS
          ===================================== */}

          <section className="side-card">

            <div className="side-card-title">

              <div className="side-card-icon">
                ✓
              </div>

              <h3>
                Attachment status
              </h3>

            </div>

            <div className="attachment-status-box">

              <div className="status-dot">
                <span></span>
              </div>

              <div>
                <strong>
                  {user.attachmentStatus}
                </strong>

                <p>
                  We are looking for suitable
                  opportunities
                </p>
              </div>

            </div>

          </section>

          {/* =====================================
              DOCUMENT SUMMARY
          ===================================== */}

          <section className="side-card">

            <div className="side-card-title">

              <div className="side-card-icon">
                DOC
              </div>

              <h3>
                Resume
              </h3>

            </div>

            <div className="resume-summary">

              <div className="resume-file-icon">
                DOCX
              </div>

              <div>
                <strong>
                  {user.cvName ||
                    "No CV uploaded"}
                </strong>

                <span>
                  Curriculum Vitae •{" "}
                  {user.cvUploaded
                    ? "Uploaded"
                    : "Not uploaded"}
                </span>
              </div>

            </div>

            {user.cvUploaded && (
              <button
                className="resume-view-button"
                onClick={handleViewCV}
              >
                View CV
              </button>
            )}

          </section>

          {/* =====================================
              INSURANCE SUMMARY
          ===================================== */}

          <section className="side-card">

            <div className="side-card-title">

              <div className="side-card-icon">
                ✓
              </div>

              <h3>
                Insurance
              </h3>

            </div>

            <div className="insurance-summary">

              <div
                className={
                  user.insurance
                    ? "summary-insurance-icon insured-icon"
                    : "summary-insurance-icon uninsured-icon"
                }
              >
                {user.insurance
                  ? "✓"
                  : "!"}
              </div>

              <div>

                <strong>
                  {user.insurance
                    ? "Insurance covered"
                    : "No insurance cover"}
                </strong>

                {user.insurance &&
                  user.insuranceProvider && (
                    <span>
                      Provider:{" "}
                      {user.insuranceProvider}
                    </span>
                  )}

              </div>

            </div>

          </section>

        </div>

      </div>

      {/* =====================================
          ACCOUNT ACTIONS
      ===================================== */}

      <div className="account-actions">

        <button className="secondary-action">
          Change Password
        </button>

        <button className="logout-action">
          Log out
        </button>

      </div>

    </div>
  );
};

// =========================================
// SECTION TITLE
// =========================================

const SectionTitle = ({
  number,
  title,
  description,
}) => {
  return (
    <div className="section-title">

      <span className="section-number">
        {number}
      </span>

      <div>
        <h2>
          {title}
        </h2>

        <p>
          {description}
        </p>
      </div>

    </div>
  );
};

// =========================================
// EDITABLE DETAIL
// =========================================

const EditableDetail = ({
  label,
  value,
  editMode,
  onChange,
  type = "text",
}) => {
  return (
    <div className="detail-item">

      <span>
        {label}
      </span>

      {editMode ? (
        <input
          className="edit-input"
          type={type}
          value={value || ""}
          onChange={(e) =>
            onChange(
              e.target.value
            )
          }
        />
      ) : (
        <strong>
          {value || "Not provided"}
        </strong>
      )}

    </div>
  );
};

export default AccountDetails;